import React, { FC, useEffect, useRef, useState } from 'react';
import {
	FaArrowLeft,
	FaArrowRight,
	FaAward,
	FaBell,
	FaCheck,
	FaCloudUploadAlt,
	FaFileImport,
	FaLayerGroup,
	FaMagic,
	FaPlus,
	FaRegPaperPlane,
	FaTimes,
} from 'react-icons/fa';

interface NewFeatureTutorialProps {
	onClose: () => void;
	onDisplayed?: () => void;
}

type SlideKind =
	| 'documents'
	| 'drawing-upload'
	| 'packages'
	| 'bid-list-review'
	| 'package'
	| 'checklist'
	| 'publish'
	| 'leveling';
interface TutorialSlide {
	kind: SlideKind;
	title: string;
	description: string;
	browserTitle: string;
	screenLabel: string;
	notes: { title: string; copy: string }[];
}

const Callout: FC<{ number: number }> = ({ number }) => (
	<span className="feature-tutorial-callout" aria-hidden="true">
		{number}
	</span>
);

const StaticButton: FC<{ children: React.ReactNode; callout?: number; primary?: boolean }> = ({
	children,
	callout,
	primary,
}) => (
	<div className={`feature-tutorial-ui-button${primary ? ' primary' : ''}`}>
		{children}
		{callout && <Callout number={callout} />}
	</div>
);

const PackageTabs: FC<{ active: string; markScope?: boolean }> = ({ active, markScope }) => (
	<div className="feature-tutorial-ui-tabs package-tabs">
		{[
			'Coverage Overview',
			'Scope Locations Found',
			'Bid Leveling Checklist',
			'Bid Leveling',
			'Contract Inclusions',
		].map((tab) => (
			<span className={active === tab ? 'active' : ''} key={tab}>
				{tab}
				{markScope && tab === 'Scope Locations Found' && <Callout number={3} />}
			</span>
		))}
	</div>
);

const PackageHeading = () => (
	<div className="feature-tutorial-ui-package-heading">
		<b>09</b>
		<div>
			<small>BID PACKAGE</small>
			<h3>09 Drywall</h3>
		</div>
		<em>Package color</em>
	</div>
);

const DocumentsMock = () => (
	<div className="feature-tutorial-ui-workspace">
		<div className="feature-tutorial-ui-title">
			<div>
				<small>PROJECT WORKSPACE</small>
				<h3>Contract Documents</h3>
			</div>
			<em>Project setup</em>
		</div>
		<div className="feature-tutorial-ui-document-list">
			{[
				[
					'Contract Drawings',
					'Manage drawing releases and the drawings currently included in this project.',
					'Documents',
					'No drawing versions have been uploaded.',
				],
				[
					'Specifications',
					'Manage specification releases and the specifications currently included in this project.',
					'Specifications',
					'No specification versions have been uploaded.',
				],
				[
					'Supplemental Bid Documents',
					'ScrubPlan reviews these documents and adds applicable scope to the package checklists.',
					'Bid Document',
					'No supplemental bid documents have been uploaded.',
				],
			].map((item, index) => (
				<section key={item[0]}>
					<div>
						<strong>{item[0]}</strong>
						<small>{item[1]}</small>
					</div>
					<StaticButton callout={index + 1}>
						<FaPlus aria-hidden="true" /> {item[2]}
					</StaticButton>
					<p>{item[3]}</p>
				</section>
			))}
		</div>
	</div>
);

const DrawingUploadMock = () => (
	<div className="feature-tutorial-ui-workspace upload-workspace">
		<div className="feature-tutorial-ui-modal">
			<header>
				<h3>Upload Contract Drawings</h3>
				<FaTimes aria-hidden="true" />
			</header>
			<div className="feature-tutorial-ui-modal-body">
				<div className="feature-tutorial-ui-dropzone">
					<FaCloudUploadAlt aria-hidden="true" />
					<div>
						<strong>Drop contract drawings here</strong>
						<span>or choose one or more PDF files from your computer</span>
					</div>
					<em>PDF&nbsp;&nbsp; Issued for Bid Drawings.pdf</em>
				</div>
				<section className="feature-tutorial-ui-upload-details">
					<header>
						<div>
							<small>UPLOAD DETAILS</small>
							<h4>Identify this document set</h4>
						</div>
						<strong>1 PDF selected</strong>
					</header>
					<div className="feature-tutorial-ui-form-grid">
						{/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
						<label className="wide">
							<span>Use an existing version</span>
							<em>Select a previous version</em>
						</label>
						{/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
						<label className="wide">
							<span>Project Area</span>
							<em>
								Select or create a project area
								<Callout number={2} />
							</em>
						</label>
						{/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
						<label>
							<span>Drawing version</span>
							<em>
								Issued for Bid
								<Callout number={1} />
							</em>
						</label>
						{/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
						<label>
							<span>Drawing date</span>
							<em>08/01/2026</em>
						</label>
					</div>
					<div className="feature-tutorial-ui-form-actions">
						<StaticButton primary>Continue</StaticButton>
					</div>
				</section>
			</div>
		</div>
	</div>
);

const BidPackagesMock = () => (
	<div className="feature-tutorial-ui-workspace">
		<div className="feature-tutorial-ui-actions">
			<div>
				<FaMagic />
				<span>
					<strong>Auto-Analyze</strong>
					<small>Create bid packages from the drawings.</small>
				</span>
				<Callout number={1} />
			</div>
			<div>
				<FaFileImport />
				<span>
					<strong>Import Bid List</strong>
					<small>Import bid packages and bidders from another project.</small>
				</span>
				<Callout number={2} />
			</div>
			<div className="primary">
				<FaRegPaperPlane />
				<span>
					<strong>Publish Project to Bidders</strong>
					<small>Review recipients and send invitations.</small>
				</span>
				<Callout number={3} />
			</div>
		</div>
		<section className="feature-tutorial-ui-table-card">
			<header>
				<div>
					<small>PROJECT WORKSPACE</small>
					<h3>Bid Packages</h3>
				</div>
				<em>Bid Due Date&nbsp;&nbsp; Aug 21, 2026</em>
			</header>
			<div className="feature-tutorial-ui-table five">
				<div className="head">
					<span>Package</span>
					<span>Package Lead</span>
					<span>Bidders in Bid List</span>
					<span>Companies Invited</span>
					<span>Actions</span>
				</div>
				<div>
					<strong>09 Drywall</strong>
					<span>Morgan Lead</span>
					<span>3</span>
					<span>0</span>
					<span>Actions</span>
				</div>
				<div>
					<strong>26 Electrical</strong>
					<span>Unassigned</span>
					<span>2</span>
					<span>0</span>
					<span>Actions</span>
				</div>
			</div>
		</section>
	</div>
);

const BidListReviewMock = () => (
	<div className="feature-tutorial-ui-workspace audit-workspace">
		<div className="feature-tutorial-ui-audit-dialog">
			<header>
				<small>SMART BIDDER REVIEW</small>
				<h3>Bid list audit recommendations</h3>
				<p>
					Use the checkboxes to add bidders to this project. Each recommendation is based on your
					organization&apos;s recent bidding history.
				</p>
			</header>
			<div className="feature-tutorial-ui-audit-results">
				<section>
					<h4>09 Drywall</h4>
					<div className="feature-tutorial-ui-audit-suggestion">
						<i className="feature-tutorial-ui-audit-checkbox">
							<Callout number={1} />
						</i>
						<span>
							<strong>Premier Interiors</strong>
							<small>Submitted bids on four similar interior renovation projects.</small>
						</span>
					</div>
					<div className="feature-tutorial-ui-audit-suggestion selected">
						<i className="feature-tutorial-ui-audit-checkbox">
							<FaCheck />
						</i>
						<span>
							<strong>Metro Wall Systems</strong>
							<small>Frequently bids this office&apos;s healthcare drywall packages.</small>
						</span>
					</div>
				</section>
				<section>
					<h4>26 Electrical</h4>
					<div className="feature-tutorial-ui-audit-suggestion">
						<i className="feature-tutorial-ui-audit-checkbox" />
						<span>
							<strong>BrightLine Electric</strong>
							<small>Strong participation on comparable medical office projects.</small>
						</span>
					</div>
				</section>
			</div>
			<footer>
				<span>Cancel</span>
				<strong>Add bidders and send invites</strong>
			</footer>
		</div>
	</div>
);

const CreatedPackageMock = () => (
	<div className="feature-tutorial-ui-workspace">
		<PackageHeading />
		<div className="feature-tutorial-ui-package-tabs-row">
			<PackageTabs active="Coverage Overview" markScope />
			<StaticButton callout={2} primary>
				<FaLayerGroup /> Interactive Review
			</StaticButton>
		</div>
		<div className="feature-tutorial-ui-coverage-metrics">
			{[
				['Invites Sent', '3'],
				['Email Invite Opened', '2'],
				['Viewed Link', '2'],
				['Confirmed Bidding', '1'],
				['Bids Received', '0'],
				['Not Bidding', '0'],
			].map((metric) => (
				<div key={metric[0]}>
					<span>{metric[0]}</span>
					<strong>{metric[1]}</strong>
				</div>
			))}
		</div>
		<section className="feature-tutorial-ui-coverage-summary">
			<div>
				<h4>Coverage at a glance</h4>
				<p>Invite, view, and response counts update as bidder companies use their package link.</p>
			</div>
			<div className="feature-tutorial-ui-reminder">
				<FaBell />
				<span>
					<strong>Reminder Emails</strong>
					<small>View or customize the project schedule</small>
				</span>
			</div>
		</section>
		<section className="feature-tutorial-ui-bidders">
			<header>
				<div>
					<h4>Bidders</h4>
					<p>Manage invited companies and review their current bidding status and contacts.</p>
				</div>
				<StaticButton callout={1} primary>
					<FaPlus /> Bidder
				</StaticButton>
			</header>
			<div className="feature-tutorial-ui-bidder-overview">
				<div>
					<small>INVITED COMPANIES</small>
					<strong>3 bidders</strong>
				</div>
				<div>
					<strong>Premier Interiors</strong>
					<span>Invite sent</span>
				</div>
				<div>
					<strong>Allied Drywall</strong>
					<span>Confirmed bidding</span>
				</div>
			</div>
		</section>
	</div>
);

const ChecklistMock = () => (
	<div className="feature-tutorial-ui-workspace">
		<PackageHeading />
		<PackageTabs active="Bid Leveling Checklist" />
		<section className="feature-tutorial-ui-checklist">
			<header>
				<div>
					<small>BID PREPARATION</small>
					<h3>Bid Leveling Checklist</h3>
					<p>
						Create consistent pricing rows and scope confirmations for every bidder on{' '}
						<strong>09 Drywall</strong>.
					</p>
				</div>
				<em>Export</em>
			</header>
			{[
				{
					title: 'Enter Base Bid Here',
					rows: ['Cold-formed metal framing', 'Gypsum board assemblies'],
					button: '+ Line Item',
					base: true,
				},
				{
					title: 'Enter Alternates Here',
					rows: ['Alternate 1 - Level 2 finish upgrade', 'Alternate 2 - Moisture-resistant board'],
					button: '+ Alternate',
					base: false,
				},
			].map((section) => (
				<div className="feature-tutorial-ui-pricing-section" key={section.title}>
					<div className="feature-tutorial-ui-pricing-title">
						<strong>{section.title}</strong>
						<span>Define what bidders should price.</span>
					</div>
					<div className="feature-tutorial-ui-pricing-grid">
						<div className="head">
							<strong>Description</strong>
							<strong>Quantity</strong>
							<strong>Unit Cost</strong>
							<strong>Total Cost</strong>
						</div>
						{section.rows.map((row) => (
							<div key={row}>
								<span>{row}</span>
								<span />
								<span />
								<span />
							</div>
						))}
						{section.base && (
							<div className="total">
								<strong>Base Bid</strong>
								<span />
								<span />
								<strong>$0.00</strong>
							</div>
						)}
					</div>
					<em>{section.button}</em>
				</div>
			))}
			<div className="feature-tutorial-ui-create compact">
				<div>
					<strong>Scope Checklist</strong>
					<p>Create the scope checklist for 09 Drywall.</p>
				</div>
				<StaticButton callout={1} primary>
					Create
				</StaticButton>
			</div>
		</section>
	</div>
);

const PublishMock = () => (
	<div className="feature-tutorial-ui-workspace publish-workspace">
		<div className="feature-tutorial-ui-publish-dialog">
			<header>
				<h3>Publish Project to Bidders</h3>
				<p>Review who will receive an invitation for Central Medical Office.</p>
			</header>
			<div className="feature-tutorial-ui-publish-body">
				<div className="feature-tutorial-ui-ready">
					<FaCheck />
					<span>
						<strong>Ready to publish</strong>
						<small>2 packages / 5 bidders</small>
					</span>
				</div>
				<div className="feature-tutorial-ui-access">
					<strong>Bidder workspace access</strong>
					<p>
						Choose which review tools invited bidders can use. All options are included by default.
					</p>
				</div>
				{[
					[
						'Include scope locations',
						'Let bidders quickly review drawings with their scope locations highlighted.',
					],
					[
						'Include quote review',
						'ScrubPlan performs the first review, identifies discrepancies, and asks bidders to address comments before you receive the bid.',
					],
					[
						'Allow grouping-level Yes/No',
						'Let bidders answer an entire visible scope grouping at once, then adjust individual line items.',
					],
				].map((option, index) => (
					<div className="feature-tutorial-ui-option" key={option[0]}>
						<i>
							<FaCheck />
						</i>
						<span>
							<strong>{option[0]}</strong>
							<small>{option[1]}</small>
						</span>
						<Callout number={index + 1} />
					</div>
				))}
			</div>
			<footer>
				<span>Cancel</span>
				<strong>Publish and Send Invitations</strong>
			</footer>
		</div>
	</div>
);

const LevelingMock = () => (
	<div className="feature-tutorial-ui-workspace">
		<PackageHeading />
		<PackageTabs active="Bid Leveling" />
		<div className="feature-tutorial-ui-leveling-hero">
			<div>
				<small>LIVE BID COMPARISON</small>
				<strong>Bid Leveling</strong>
			</div>
			<em>Sort by cost: Low to high</em>
		</div>
		<div className="feature-tutorial-ui-leveling">
			<div className="head">
				<div>
					<small>REQUESTED SCOPE</small>
					<strong>Line Item</strong>
				</div>
				<div>
					<i>
						<FaAward /> Awarded Bidder
					</i>
					<small>SUBMITTED BID</small>
					<strong>Allied Drywall</strong>
					<b>$486,200</b>
					<Callout number={3} />
				</div>
				<div>
					<i>Awarded Bidder</i>
					<small>SUBMITTED BID</small>
					<strong>Premier Interiors</strong>
					<b>$512,750</b>
					<em>Quote Discrepancies Found</em>
					<Callout number={2} />
				</div>
			</div>
			<div className="section">
				<strong>Base Bid Pricing</strong>
				<span />
				<span />
			</div>
			<div>
				<span>Cold-formed metal framing</span>
				<span>$132,400</span>
				<span>$139,000</span>
			</div>
			<div>
				<span>Gypsum board assemblies</span>
				<span>$312,800</span>
				<span>$329,750</span>
			</div>
			<div className="total">
				<strong>Base Bid</strong>
				<strong>$486,200</strong>
				<strong>$512,750</strong>
				<Callout number={1} />
			</div>
			<div className="section">
				<strong>Scope Checklist</strong>
				<span />
				<span />
			</div>
			<div>
				<span>Include all rated wall assemblies</span>
				<span className="yes">YES</span>
				<span className="yes">YES</span>
			</div>
		</div>
	</div>
);

const SlideMock: FC<{ kind: SlideKind }> = ({ kind }) => {
	if (kind === 'documents') return <DocumentsMock />;
	if (kind === 'drawing-upload') return <DrawingUploadMock />;
	if (kind === 'packages') return <BidPackagesMock />;
	if (kind === 'bid-list-review') return <BidListReviewMock />;
	if (kind === 'package') return <CreatedPackageMock />;
	if (kind === 'checklist') return <ChecklistMock />;
	if (kind === 'publish') return <PublishMock />;
	return <LevelingMock />;
};

const slides: TutorialSlide[] = [
	{
		kind: 'documents',
		title: 'Start with the contract documents',
		description:
			'Upload the project documents ScrubPlan will use to build bid packages and locate scope.',
		browserTitle: 'Contract Documents',
		screenLabel: 'Contract Documents workspace with three upload controls',
		notes: [
			{
				title: 'Upload contract drawings',
				copy: 'Add the drawing set so ScrubPlan can identify bid packages and scope locations.',
			},
			{
				title: 'Upload specifications',
				copy:
					'Add the project manual so specification requirements can be included in each checklist.',
			},
			{
				title: 'Add supplemental bid documents',
				copy:
					'ScrubPlan reviews schedules, logistics plans, alternates, and other supplemental bid documents, then adds applicable scope to the package checklists.',
			},
		],
	},
	{
		kind: 'drawing-upload',
		title: 'Identify the drawing set',
		description:
			'After dropping in a PDF, define the version and optional project area before continuing.',
		browserTitle: 'Upload Contract Drawings',
		screenLabel: 'Drawing upload modal after one PDF has been selected',
		notes: [
			{
				title: 'Define the document version',
				copy: 'Use a recognizable release name, such as Issued for Bid, Addendum 1, or Revision 2.',
			},
			{
				title: 'Separate project areas when needed',
				copy:
					'Define a project area when distinct buildings, wings, or phases need to be scoped and priced separately.',
			},
		],
	},
	{
		kind: 'packages',
		title: 'Create the bid packages',
		description:
			'Create bid packages by initiating Auto-Analyze or by importing bid packages from another project.',
		browserTitle: 'Bid Packages',
		screenLabel: 'Bid Packages workspace with analysis, import, and publish controls',
		notes: [
			{
				title: 'Auto-Analyze',
				copy: 'Initiate Auto-Analyze to create bid packages from the project drawings.',
			},
			{
				title: 'Import Bid List',
				copy: 'Alternatively, import bid packages and bidders from another project.',
			},
			{
				title: 'Publish Project to Bidders',
				copy: 'Bid invitations can be sent after every package has at least one bidder assigned.',
			},
		],
	},
	{
		kind: 'bid-list-review',
		title: "Review ScrubPlan's bid list suggestions",
		description:
			"Before invitations are sent, review additional bidders suggested from your organization's recent bidding history.",
		browserTitle: 'Bid List Review',
		screenLabel: 'Bid list review modal with sample bidder suggestions and selection checkboxes',
		notes: [
			{
				title: 'Select bidders to add',
				copy: 'Use the checkboxes to select the bidders you want ScrubPlan to add to the bid list.',
			},
		],
	},
	{
		kind: 'package',
		title: 'Review each created package',
		description:
			'Add bidders and use the drawing-review tools to verify where the package scope was found.',
		browserTitle: '09 Drywall',
		screenLabel: 'Created 09 Drywall package with bidder and scope review controls',
		notes: [
			{
				title: 'Add bidders',
				copy: 'Select + Bidder to add companies from your vendor network to this package.',
			},
			{
				title: 'Open Interactive Review',
				copy:
					'Review the package scope visually across the drawing set and confirm the highlighted locations.',
			},
			{
				title: 'Open Scope Locations Found',
				copy: 'Select this tab to view the locations of the package scope on the drawings.',
			},
		],
	},
	{
		kind: 'publish',
		title: 'Choose what bidders can review',
		description:
			'Confirm the bidder workspace tools that will be included when the project is published.',
		browserTitle: 'Publish Project to Bidders',
		screenLabel: 'Publish Project to Bidders modal with workspace access options',
		notes: [
			{
				title: 'Include scope locations',
				copy:
					'Bidders will be able to quickly review the drawings with highlights of their scope locations.',
			},
			{
				title: 'Include quote review',
				copy:
					'ScrubPlan performs the first review to identify discrepancies and has bidders address comments before you receive the bid.',
			},
			{
				title: 'Allow grouping-level Yes/No',
				copy:
					'Bidders can answer an entire visible scope grouping at once and then adjust individual line items.',
			},
		],
	},
	{
		kind: 'checklist',
		title: 'Create the bid leveling checklist',
		description:
			'Define base-bid rows and alternates, then review the scope checklist used for bidder confirmations.',
		browserTitle: '09 Drywall | Bid Leveling Checklist',
		screenLabel: 'Bid Leveling Checklist tab with base bid and alternate pricing rows',
		notes: [
			{
				title: 'Create and review the results',
				copy:
					'Review the base bid and alternate rows, then select Create for the scope checklist. Bidders complete this information in their bid submission forms.',
			},
		],
	},
	{
		kind: 'leveling',
		title: 'Compare submitted bids side by side',
		description:
			'Use the bid leveling sheet to compare totals, scope responses, and quote differences from every bidder.',
		browserTitle: '09 Drywall | Bid Leveling',
		screenLabel: 'Bid Leveling tab with a sample sheet containing two bidders',
		notes: [
			{
				title: 'Compare every bid on the same rows',
				copy: 'Review base-bid totals, alternates, and scope confirmations side by side.',
			},
			{
				title: 'Review quote discrepancies',
				copy:
					"Open the discrepancy notice to resolve differences found in the bidder's uploaded quote.",
			},
			{
				title: 'Record the award decision',
				copy: 'Select Awarded Bidder on the chosen company when the leveling review is complete.',
			},
		],
	},
];

const NewFeatureTutorial: FC<NewFeatureTutorialProps> = ({ onClose, onDisplayed }) => {
	const displayed = useRef(false);
	const [slideIndex, setSlideIndex] = useState(0);
	const slide = slides[slideIndex];
	const isLast = slideIndex === slides.length - 1;

	useEffect(() => {
		if (!displayed.current) {
			displayed.current = true;
			if (onDisplayed) onDisplayed();
		}
	}, [onDisplayed]);

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') onClose();
			if (event.key === 'ArrowLeft') setSlideIndex((current) => Math.max(0, current - 1));
			if (event.key === 'ArrowRight')
				setSlideIndex((current) => Math.min(slides.length - 1, current + 1));
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [onClose]);

	return (
		<div
			className="feature-tutorial-overlay"
			role="dialog"
			aria-modal="true"
			aria-label="Bid Management Tutorial"
		>
			<div className="feature-tutorial-dialog">
				<header className="feature-tutorial-header">
					<div>
						<span className="feature-tutorial-kicker">
							<FaMagic /> Project setup tutorial
						</span>
						<h2>{slide.title}</h2>
						<p>{slide.description}</p>
					</div>
					<button
						aria-label="Close tutorial"
						className="feature-tutorial-close"
						onClick={onClose}
						type="button"
					>
						<FaTimes />
					</button>
				</header>
				<div className="feature-tutorial-content">
					<div className="feature-tutorial-screen" aria-label={slide.screenLabel}>
						<div className="feature-tutorial-browser-bar">
							<span />
							<span />
							<span />
							<strong>{slide.browserTitle}</strong>
						</div>
						<div className="feature-tutorial-page">
							<SlideMock kind={slide.kind} />
						</div>
					</div>
					<aside className="feature-tutorial-notes" aria-label="Slide explanations">
						{slide.notes.map((note, index) => (
							<div key={note.title}>
								<span>{index + 1}</span>
								<p>
									<strong>{note.title}</strong>
									{note.copy}
								</p>
							</div>
						))}
					</aside>
				</div>
				<footer className="feature-tutorial-footer">
					<button
						aria-label="Previous tutorial slide"
						className="feature-tutorial-footer__back"
						disabled={slideIndex === 0}
						onClick={() => setSlideIndex((current) => Math.max(0, current - 1))}
						type="button"
					>
						<FaArrowLeft /> Previous
					</button>
					<div
						className="feature-tutorial-progress"
						aria-label={`Slide ${slideIndex + 1} of ${slides.length}`}
					>
						<span>
							{slideIndex + 1} of {slides.length}
						</span>
						<div aria-hidden="true">
							{slides.map((item, index) => (
								<i className={index === slideIndex ? 'active' : ''} key={item.title} />
							))}
						</div>
					</div>
					<button
						aria-label={isLast ? 'Finish tutorial' : 'Next tutorial slide'}
						onClick={
							isLast
								? onClose
								: () => setSlideIndex((current) => Math.min(slides.length - 1, current + 1))
						}
						type="button"
					>
						{isLast ? 'Got it' : 'Next'} <FaArrowRight />
					</button>
				</footer>
			</div>
		</div>
	);
};

export default NewFeatureTutorial;
